'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, parseGridId } from '@/lib/utils';
import { gridPoolMetadataId } from '@/lib/grid-pool-id';
import { refreshGridSnapshot } from '@/lib/grid-snapshot-refresh';
import type { LobbyGridPage } from '@/hooks/use-lobby-grid-page';
import { useWallet } from '@/hooks/use-wallet';
import { isBoardManagerAddress } from '@/lib/admin/allow-list';
import type { GridAdapterNote } from '@/lib/types/v2';
import { resolveLobbyLiveGridId } from '@/features/lobby/data/boardroom-grid';
import { ImageFilePicker } from '@/features/lobby/ui/ImageFilePicker';
import { VerifyXField } from '@/features/lobby/ui/VerifyXField';

/**
 * Discriminated state of a creator/manager save button. Used by both
 * `CreatorBoardEditPanel` and `AdapterNoteEditor` so the button-swap
 * priority is single-source-of-truth and unit-testable.
 *
 *   - `saving`            — the save POST is in flight.
 *   - `signing-in`        — `signInActiveWallet()` is mid-SIWE.
 *                           Checked BEFORE `sign-in-required` so the
 *                           in-flight spinner copy doesn't blink back
 *                           to "Sign in with this wallet" between
 *                           click and Privy popup.
 *   - `sign-in-required`  — the wagmi-active wallet isn't part of the
 *                           current Privy session; a save would 403
 *                           server-side. Click routes to
 *                           `signInActiveWallet`.
 *   - `save`              — happy path; click runs the save POST.
 */
export type SaveButtonMode =
  | 'saving'
  | 'signing-in'
  | 'sign-in-required'
  | 'save';

export function resolveSaveButtonMode({
  isSaving,
  isSigningIn,
  isAddressLinked,
}: {
  isSaving: boolean;
  isSigningIn: boolean;
  isAddressLinked: boolean;
}): SaveButtonMode {
  if (isSaving) return 'saving';
  if (isSigningIn) return 'signing-in';
  if (!isAddressLinked) return 'sign-in-required';
  return 'save';
}

/* ════════════════════════════════════════════════════════════════════════
 * The "Edit Panel" is split into two surfaces — one rendered under the
 * board metadata (Info) card, one rendered under the Board Yield list —
 * so each card edits the metadata it actually owns. They share chrome
 * (collapsible header + pencil icon + amber border) but render different
 * bodies. Either can be opened imperatively via the `open()` handle so
 * pencil chips on the metadata cards can expand the matching panel.
 * ──────────────────────────────────────────────────────────────────────── */

export interface EditPanelHandle {
  /** Expand the panel and scroll it into view. */
  open: () => void;
}

interface BoardEditPanelProps {
  boardId: string;
  gridPage: LobbyGridPage;
  className?: string;
}

interface YieldEditPanelProps {
  boardId: string;
  gridPage: LobbyGridPage;
  className?: string;
}

function EditPanelChrome({
  open,
  onToggle,
  rootRef,
  className,
  subtitle,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  rootRef: React.MutableRefObject<HTMLDivElement | null>;
  className?: string;
  /** Optional inline subtitle rendered next to the "Edit Panel" header
   *  label. Used by `CreatorBoardEditPanel` to surface a one-liner
   *  CTA ("Finish setting up your board") when the onchain creator
   *  lands on a board whose `pool_metadata` row never persisted (e.g.
   *  the launch wizard's POST raced Privy and the retry flusher never
   *  caught up — see `usePendingMetadataFlush` for the retry path). */
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      ref={rootRef}
      className={cn(
        'rounded-[6px] border border-amber-500/30 bg-black/50 backdrop-blur-md overflow-hidden',
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left border-b border-amber-500/15 hover:bg-white/[0.03] transition-colors"
      >
        <Pencil className="h-3 w-3 text-amber-200 shrink-0" />
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-amber-100">
          Edit Panel
        </span>
        {subtitle ? (
          <span className="text-[10px] text-amber-200/80 truncate">
            · {subtitle}
          </span>
        ) : null}
        <span className="text-[9px] text-zinc-500 ml-auto">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="p-2 space-y-2">{children}</div>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * Board metadata edit panel — sits under the Info / pinned title card.
 * Edits description, image, and social links for the active grid.
 * ──────────────────────────────────────────────────────────────────────── */

export const CreatorBoardEditPanel = forwardRef<EditPanelHandle, BoardEditPanelProps>(
  function CreatorBoardEditPanel({ boardId, gridPage, className }, ref) {
    const liveId = resolveLobbyLiveGridId(boardId);
    const queryClient = useQueryClient();
    const wallet = useWallet();
    const { getAccessToken, isAddressLinked, signInActiveWallet } = wallet;
    // Mirrors the server's `requireGridCreator` manager-bypass path:
    // only wallets on `BOARD_MANAGER_ADDRESSES` may rewrite the
    // board's display name. The bootstrap list is bundled into the
    // client build (env-var augmentation only resolves server-side),
    // so the hard-coded ops wallet picks up the elevated UI without
    // a deploy and without a round-trip flicker on first paint.
    const isManager = isBoardManagerAddress(wallet.address ?? null);

    const grid = gridPage.grid;
    const manager = grid?.managerAddress;

    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    // In-flight flag for the SIWE sign-in path. Mirrors `chat-panel.tsx`
    // (lines 193-213): we ignore the discriminated `signInActiveWallet`
    // result on purpose — on any outcome the next render evaluates
    // `isAddressLinked` fresh, so the button either flips back to
    // "Save board metadata" (success) or stays as the sign-in CTA the
    // user can retry. No bespoke error copy.
    const [isSigningIn, setIsSigningIn] = useState(false);

    // Manager-only board name input. Creators can't rename their own
    // board after launch (the server enforces this in
    // `/api/grid/[ugm]/[gridId]/metadata`), so we only render the
    // input — and only send `name` in the save payload — when the
    // viewer is on the manager allow-list.
    const [boardName, setBoardName] = useState('');
    const [boardDesc, setBoardDesc] = useState('');
    const [boardImage, setBoardImage] = useState('');
    /**
     * OAuth-verified X handle. Empty string = no X attached. The save
     * payload sends `twitterHandle` instead of the legacy `twitterUrl`
     * — see the metadata route's `isHandleVerified` gate. For boards
     * created before the gate landed, we parse the legacy
     * `twitterUrl` into a handle so the pill renders something
     * familiar; the `VerifyXField` will mark it `Unverified` until
     * the creator re-OAuths.
     */
    const [boardXHandle, setBoardXHandle] = useState('');
    const [boardTg, setBoardTg] = useState('');
    const [boardDc, setBoardDc] = useState('');
    const [boardSite, setBoardSite] = useState('');
    const [boardSaving, setBoardSaving] = useState(false);
    const [boardMsg, setBoardMsg] = useState<string | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setOpen(true);
          requestAnimationFrame(() => {
            rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        },
      }),
      [],
    );

    // Re-sync the form from the snapshot only when its CONTENT changes —
    // initial load and the post-save refresh both carry new values, so the
    // form still updates then. A reference-only change (react-query's
    // window-focus refetch, which fires when the OS file picker blurs +
    // refocuses the window mid-upload) returns identical content, so the
    // snapshot matches and we skip the reset — otherwise it would wipe the
    // just-uploaded board image (the same bug the yield-copy editor had).
    const syncedSnapRef = useRef<string | null>(null);
    useEffect(() => {
      if (!grid) return;
      const s = gridPage.rawSocialLinks;
      // Prefer the canonical verified handle (post-gate rows), then
      // fall back to parsing the legacy `twitterUrl` so existing
      // boards don't appear "empty" when their creator opens this
      // panel for the first time after the OAuth gate ships.
      const handle = s?.twitterHandle ?? extractHandleFromTwitterUrl(s?.twitterUrl) ?? '';
      const snap = JSON.stringify([
        grid.name,
        grid.description,
        grid.imageUrl,
        handle,
        s?.telegramUrl,
        s?.discordUrl,
        s?.websiteUrl,
      ]);
      if (syncedSnapRef.current === snap) return;
      syncedSnapRef.current = snap;
      setBoardName(grid.name ?? '');
      setBoardDesc(grid.description ?? '');
      setBoardImage(grid.imageUrl ?? '');
      setBoardXHandle(handle);
      setBoardTg(s?.telegramUrl ?? '');
      setBoardDc(s?.discordUrl ?? '');
      setBoardSite(s?.websiteUrl ?? '');
    }, [grid, gridPage.rawSocialLinks]);

    /**
     * Heuristic for "the `pool_metadata` row never landed for this
     * grid". We can't read the row directly from the lobby, but the
     * snapshot exposes every field the creator would have set in the
     * launch wizard:
     *
     *   - `boardDescription` → `pool_metadata.description`
     *   - `rawSocialLinks` → `twitter_handle` / `telegram_url` / etc.
     *   - `grid.imageUrl` → `pool_metadata.image_url` when present;
     *     otherwise resolves to the `/api/token-icon/...` proxy
     *     fallback (see `resolveGridImage` step 6).
     *
     * If all three slots are empty we treat the row as missing and
     * auto-surface this panel so the onchain creator gets an obvious
     * recovery path on first reload — without having to discover the
     * pencil chip themselves.
     */
    const metadataRowEmpty = useMemo(() => {
      if (!grid) return false;
      const desc = (grid.description ?? '').trim();
      const img = grid.imageUrl ?? '';
      const hasCustomImage = img && !img.startsWith('/api/token-icon/');
      const s = gridPage.rawSocialLinks;
      const hasSocial = !!(
        s?.twitterHandle ||
        s?.twitterUrl ||
        s?.telegramUrl ||
        s?.discordUrl ||
        s?.websiteUrl
      );
      return !desc && !hasCustomImage && !hasSocial;
    }, [grid, gridPage.rawSocialLinks]);

    /**
     * One-shot auto-open for the empty-metadata recovery flow. We
     * latch a ref the first time we trigger so a creator who closes
     * the panel manually doesn't see it pop back open every render.
     * The latch resets per-mount, which matches the user's intent —
     * if they navigate away and come back later with the row still
     * empty, we should nudge them again.
     */
    const didAutoOpenRef = useRef(false);
    useEffect(() => {
      if (didAutoOpenRef.current) return;
      if (!gridPage.isCreator) return;
      if (!grid) return;
      if (!metadataRowEmpty) return;
      didAutoOpenRef.current = true;
      setOpen(true);
      requestAnimationFrame(() => {
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }, [gridPage.isCreator, grid, metadataRowEmpty]);

    const handleSignIn = useCallback(async () => {
      setIsSigningIn(true);
      try {
        await signInActiveWallet();
      } finally {
        setIsSigningIn(false);
      }
    }, [signInActiveWallet]);

    const saveBoardMeta = useCallback(async () => {
      if (!liveId || !manager) return;
      setBoardMsg(null);
      setBoardSaving(true);
      try {
        const { managerAddress, numericId } = parseGridId(liveId);
        const token = await getAccessToken();
        const res = await fetch(`/api/grid/${managerAddress}/${numericId}/metadata`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            // Only managers may rename a board after launch — the
            // server-side metadata route silently drops any
            // creator-supplied `name` once the row already has one.
            // We still send `undefined` from a creator's payload so
            // a stale form value can't accidentally re-pin a stale
            // name client-side either.
            name: isManager ? (boardName.trim() || undefined) : undefined,
            description: boardDesc.trim() || undefined,
            imageUrl: boardImage.trim() || undefined,
            // Pass the verified handle (or `''` to clear). Sending
            // `''` is meaningful: the metadata route treats it as an
            // explicit clear and drops both `twitter_handle` and
            // `twitter_url` from the row.
            twitterHandle: boardXHandle.trim(),
            telegramUrl: boardTg.trim() || undefined,
            discordUrl: boardDc.trim() || undefined,
            websiteUrl: boardSite.trim() || undefined,
          }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j?.error || j?.message || `HTTP ${res.status}`);
        }
        // Pull the post-save snapshot directly past the edge cache so
        // the panel re-renders with the freshly-saved description /
        // image / socials immediately. We only fall back to
        // `invalidateQueries` when that fresh fetch fails — kicking off
        // an unconditional refetch after `setQueryData` lets the
        // regular query path (no `?fresh=1`) be served from the
        // browser's HTTP cache (`Cache-Control: max-age=10`) and
        // overwrite the freshly-saved data with the pre-save snapshot,
        // which is the "saved but didn't update without a refresh"
        // symptom users were hitting.
        const refreshed = await refreshGridSnapshot(queryClient, liveId);
        if (!refreshed) {
          await queryClient.invalidateQueries({ queryKey: ['v2-grid', liveId] });
        }
        setBoardMsg('Saved');
      } catch (e) {
        setBoardMsg(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setBoardSaving(false);
      }
    }, [
      liveId,
      manager,
      boardName,
      boardDesc,
      boardImage,
      boardXHandle,
      boardTg,
      boardDc,
      boardSite,
      isManager,
      getAccessToken,
      queryClient,
    ]);

    if (!gridPage.isCreator || !liveId || !grid) return null;

    return (
      <EditPanelChrome
        open={open}
        onToggle={() => setOpen((o) => !o)}
        rootRef={rootRef}
        className={className}
        subtitle={metadataRowEmpty ? 'Finish setting up your board' : undefined}
      >
        <div className="space-y-2 text-[11px]">
          {/* Manager-only Name input. Creators can't rename their own
              board (server enforces in the metadata route — any
              creator-supplied `name` is silently dropped once the row
              already has one), so the field is intentionally hidden
              from them. The 42-char cap mirrors the launch wizard's
              `NAME_MAX` (Identity step validator). */}
          {isManager && (
            <Field label="Name (manager)">
              <Input
                value={boardName}
                maxLength={42}
                onChange={(e) => setBoardName(e.target.value)}
                placeholder="Board name"
                className="h-8 text-[11px] border-amber-400/30 bg-black/40"
              />
            </Field>
          )}
          <Field label="Description">
            <textarea
              value={boardDesc}
              maxLength={280}
              rows={2}
              onChange={(e) => setBoardDesc(e.target.value)}
              className="w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-amber-50/95"
            />
          </Field>
          <Field label="Image">
            <ImageFilePicker
              value={boardImage}
              onChange={(url) => setBoardImage(url ?? '')}
              size={64}
              allowPasteUrl
            />
          </Field>
          <Field label="X / Twitter">
            <VerifyXField
              value={boardXHandle}
              onChange={setBoardXHandle}
              size="sm"
              disabled={boardSaving}
            />
          </Field>
          <Field label="Telegram">
            <Input
              value={boardTg}
              onChange={(e) => setBoardTg(e.target.value)}
              className="h-8 text-[11px] border-white/10 bg-black/40"
            />
          </Field>
          <Field label="Discord">
            <Input
              value={boardDc}
              onChange={(e) => setBoardDc(e.target.value)}
              className="h-8 text-[11px] border-white/10 bg-black/40"
            />
          </Field>
          <Field label="Website">
            <Input
              value={boardSite}
              onChange={(e) => setBoardSite(e.target.value)}
              className="h-8 text-[11px] border-white/10 bg-black/40"
            />
          </Field>
          {boardMsg && <p className="text-[10px] text-zinc-400">{boardMsg}</p>}
          {/* Same button element / className as the standalone "Save"
              CTA — only the label, click handler, and disabled flag
              swap based on the resolved mode. When the wagmi-active
              wallet isn't part of the current Privy session, clicking
              Save would 403 server-side (`requireGridCreator` checks
              the Privy-linked address set, not the wagmi-active one),
              so we route the click into the single-SIWE session swap
              instead. On success the next render shows the real Save
              button without any layout shift. */}
          <SaveButton
            mode={resolveSaveButtonMode({
              isSaving: boardSaving,
              isSigningIn,
              isAddressLinked,
            })}
            saveLabel="Save board metadata"
            onSave={() => void saveBoardMeta()}
            onSignIn={() => void handleSignIn()}
            className="w-full py-1.5 rounded-[3px] bg-amber-500/25 border border-amber-400/40 text-[10px] font-mono uppercase tracking-wide text-amber-50 hover:bg-amber-500/35 disabled:opacity-50"
          />
        </div>
      </EditPanelChrome>
    );
  },
);

/* ════════════════════════════════════════════════════════════════════════
 * Yield-source metadata edit panel — sits under the Board Yield list.
 * Per-asset title / description / image / socials editor.
 * ──────────────────────────────────────────────────────────────────────── */

export const CreatorYieldEditPanel = forwardRef<EditPanelHandle, YieldEditPanelProps>(
  function CreatorYieldEditPanel({ boardId, gridPage, className }, ref) {
    const liveId = resolveLobbyLiveGridId(boardId);
    const queryClient = useQueryClient();
    const { getAccessToken, isAddressLinked, signInActiveWallet } = useWallet();

    const grid = gridPage.grid;
    const manager = grid?.managerAddress;

    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    // Per-panel SIWE in-flight flag. Threaded down to every
    // `<AdapterNoteEditor>` so a sign-in click on one row also
    // disables the save button on every other row (only one Privy
    // session swap can be in flight at a time, and re-clicking would
    // queue duplicate `loginOrLink` calls). Identical pattern to
    // `CreatorBoardEditPanel`.
    const [isSigningIn, setIsSigningIn] = useState(false);
    const handleSignIn = useCallback(async () => {
      setIsSigningIn(true);
      try {
        await signInActiveWallet();
      } finally {
        setIsSigningIn(false);
      }
    }, [signInActiveWallet]);

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setOpen(true);
          requestAnimationFrame(() => {
            rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        },
      }),
      [],
    );

    const poolId = useMemo(() => {
      if (!liveId || !manager) return '';
      return gridPoolMetadataId(liveId, manager);
    }, [liveId, manager]);

    const saveAdapterNote = useCallback(
      async (assetId: string, note: GridAdapterNote) => {
        if (!poolId || !liveId) return;
        const token = await getAccessToken();
        const res = await fetch(`/api/v1/pool-metadata/${encodeURIComponent(poolId)}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ adapterNotes: { [assetId]: note } }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j?.error || j?.message || `HTTP ${res.status}`);
        }
        // Mirror the board metadata flow: the v2-grid snapshot's edge
        // cache was just dropped server-side, but a `?fresh=1` round-
        // trip is the only way to bypass any stale CDN copy AND
        // immediately rehydrate the React Query cache so the per-asset
        // title / description / image flips on first render. The
        // fallback `invalidateQueries` only runs when the fresh fetch
        // fails — see the matching note in `saveBoardMeta` above.
        const refreshed = await refreshGridSnapshot(queryClient, liveId);
        if (!refreshed) {
          await queryClient.invalidateQueries({ queryKey: ['v2-grid', liveId] });
        }
      },
      [poolId, getAccessToken, queryClient, liveId],
    );

    if (!gridPage.isCreator || !liveId || !grid) return null;

    const activeAssets = (gridPage.assets ?? []).filter(
      (a) => a.active && !a.withdrawnAt,
    );

    return (
      <EditPanelChrome
        open={open}
        onToggle={() => setOpen((o) => !o)}
        rootRef={rootRef}
        className={className}
      >
        <div className="space-y-2 max-h-[280px] overflow-y-auto pr-0.5">
          {activeAssets.length === 0 ? (
            <p className="text-[10px] text-zinc-500 px-1">
              No active yield sources yet.
            </p>
          ) : (
            activeAssets.map((a) => (
              <AdapterNoteEditor
                key={a.id}
                assetId={a.id}
                assetLabel={a.name ?? a.symbol ?? a.id.slice(0, 8)}
                initial={gridPage.adapterNotes?.[a.id]}
                onSave={(note) => saveAdapterNote(a.id, note)}
                isAddressLinked={isAddressLinked}
                onSignIn={handleSignIn}
                isSigningIn={isSigningIn}
              />
            ))
          )}
        </div>
      </EditPanelChrome>
    );
  },
);

/**
 * Shared save button — used by both `CreatorBoardEditPanel` and
 * `AdapterNoteEditor`. Renders a single `<button>` whose label, click
 * handler, and disabled flag come from the resolved
 * {@link SaveButtonMode}. Same element / className regardless of
 * mode, so swapping between Save / Signing in / Sign in with this
 * wallet doesn't shift any surrounding layout — that "no jank"
 * constraint is the whole point of this component.
 */
export function SaveButton({
  mode,
  saveLabel,
  savingLabel = 'Saving…',
  onSave,
  onSignIn,
  className,
}: {
  mode: SaveButtonMode;
  /** Copy for the happy-path Save state. */
  saveLabel: string;
  /** Copy while the save POST is in flight. Defaults to "Saving…"
   *  for the board panel; the yield panel passes `'…'` to keep the
   *  inline CTA compact within the per-asset details disclosure. */
  savingLabel?: string;
  onSave: () => void | Promise<void>;
  onSignIn: () => void | Promise<void>;
  className?: string;
}) {
  // Mode → label and click handler via `switch` so a future
  // SaveButtonMode addition is a compile error in both maps. Same
  // pattern as `<LaunchCta>` — see the comment there for rationale.
  let label: string;
  switch (mode) {
    case 'saving':
      label = savingLabel;
      break;
    case 'signing-in':
      label = 'Signing in…';
      break;
    case 'sign-in-required':
      label = 'Sign in with this wallet';
      break;
    case 'save':
      label = saveLabel;
      break;
    default: {
      const _exhaustive: never = mode;
      label = _exhaustive;
    }
  }

  const handleClick = () => {
    switch (mode) {
      case 'sign-in-required':
        void onSignIn();
        return;
      case 'save':
        void onSave();
        return;
      case 'saving':
      case 'signing-in':
        // Disabled visually below; clicks shouldn't reach here.
        return;
      default: {
        const _exhaustive: never = mode;
        return _exhaustive;
      }
    }
  };

  return (
    <button
      type="button"
      disabled={mode === 'saving' || mode === 'signing-in'}
      onClick={handleClick}
      className={className}
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <Label className="text-[9px] font-mono uppercase tracking-[0.14em] text-zinc-500">
        {label}
      </Label>
      {children}
    </div>
  );
}

/**
 * Pull a `@handle` out of a stored legacy `twitterUrl` so the edit
 * panel can render an "Unverified — Verify X" pill for boards created
 * before the OAuth gate landed. Accepts both `x.com` and `twitter.com`
 * URLs and ignores trailing query/fragment / nested paths. Returns
 * null if nothing matches the canonical handle grammar.
 */
function extractHandleFromTwitterUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url.trim());
    if (!/^(www\.)?(x\.com|twitter\.com)$/i.test(parsed.hostname)) return null;
    const seg = parsed.pathname.split('/').filter(Boolean)[0];
    if (!seg) return null;
    return /^[A-Za-z0-9_]{1,15}$/.test(seg) ? seg : null;
  } catch {
    return null;
  }
}

function AdapterNoteEditor({
  assetId,
  assetLabel,
  initial,
  onSave,
  isAddressLinked,
  onSignIn,
  isSigningIn,
}: {
  assetId: string;
  assetLabel: string;
  initial?: GridAdapterNote;
  onSave: (note: GridAdapterNote) => Promise<void>;
  /** True when the wagmi-active address is linked to the current
   *  Privy session. Drives the save button copy/handler swap — see
   *  the comment on the rendered button below. */
  isAddressLinked: boolean;
  /** Single-SIWE session swap. Invoked when the user clicks the
   *  button while `!isAddressLinked`. */
  onSignIn: () => void | Promise<void>;
  /** True while {@link onSignIn} is in flight. */
  isSigningIn: boolean;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [imageUrl, setImageUrl] = useState(initial?.imageUrl ?? '');
  // Note: per-asset X URL was retired alongside board-level free-form X
  // entry — the board-level OAuth-verified `twitterHandle` is now the
  // only sanctioned channel for surfacing an X link in the lobby. Yield
  // panels keep Telegram / Discord / Site as raw user URLs (gated by the
  // external-link DYOR modal in `AdapterPanelShell`).
  const [telegramUrl, setTelegramUrl] = useState(initial?.telegramUrl ?? '');
  const [discordUrl, setDiscordUrl] = useState(initial?.discordUrl ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(initial?.websiteUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Re-sync the form from the saved copy only when its CONTENT changes
  // (first load, or a post-save refresh that returns new values) — never on
  // a mere *reference* change. `initial` is `gridPage.adapterNotes?.[assetId]`,
  // recreated on every grid refetch, and opening the OS file picker blurs +
  // refocuses the window, firing react-query's window-focus refetch
  // mid-upload. The old effect (`[initial, assetId]`) then reset `imageUrl`
  // back to the saved value, silently discarding the just-uploaded URL — which
  // is exactly why uploading appeared to work but never saved, while the paste
  // path (no file dialog → no refocus → no refetch) did. Comparing a content
  // snapshot makes an identical-content refetch a no-op so in-progress edits
  // survive. (Parent keys each editor on `asset.id`, so switching assets
  // remounts with fresh state.)
  const syncedSnapRef = useRef<string | null>(null);
  useEffect(() => {
    const snap = JSON.stringify([
      initial?.title,
      initial?.description,
      initial?.imageUrl,
      initial?.telegramUrl,
      initial?.discordUrl,
      initial?.websiteUrl,
    ]);
    if (syncedSnapRef.current === snap) return;
    syncedSnapRef.current = snap;
    setTitle(initial?.title ?? '');
    setDescription(initial?.description ?? '');
    setImageUrl(initial?.imageUrl ?? '');
    setTelegramUrl(initial?.telegramUrl ?? '');
    setDiscordUrl(initial?.discordUrl ?? '');
    setWebsiteUrl(initial?.websiteUrl ?? '');
  }, [initial]);

  return (
    <details className="rounded-[4px] border border-white/10 bg-black/30 px-2 py-1.5">
      <summary className="text-[10px] font-medium text-amber-100/90 cursor-pointer list-none flex justify-between gap-2">
        <span className="truncate">{assetLabel}</span>
        <span className="text-zinc-500 shrink-0 font-mono text-[9px]">edit</span>
      </summary>
      <div className="mt-2 space-y-1.5 pt-1 border-t border-white/5">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="h-7 text-[10px] border-white/10 bg-black/40"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={280}
          rows={2}
          placeholder="Short description"
          className="w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-[10px]"
        />
        <ImageFilePicker
          value={imageUrl}
          onChange={(url) => setImageUrl(url ?? '')}
          size={56}
          allowPasteUrl
        />
        <Input
          value={telegramUrl}
          onChange={(e) => setTelegramUrl(e.target.value)}
          placeholder="Telegram URL"
          className="h-7 text-[10px] border-white/10 bg-black/40"
        />
        <Input
          value={discordUrl}
          onChange={(e) => setDiscordUrl(e.target.value)}
          placeholder="Discord URL"
          className="h-7 text-[10px] border-white/10 bg-black/40"
        />
        <Input
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="Website URL"
          className="h-7 text-[10px] border-white/10 bg-black/40"
        />
        {msg && <p className="text-[9px] text-zinc-400">{msg}</p>}
        {/* In-place button swap — same element / className as the
            standalone "Save" CTA. When the wagmi-active
            wallet isn't part of the Privy session, the save would 403
            on `/api/v1/pool-metadata` (the route enforces the same
            creator/manager check as the board metadata route), so we
            route the click into `signInActiveWallet` instead. */}
        <SaveButton
          mode={resolveSaveButtonMode({
            isSaving: busy,
            isSigningIn,
            isAddressLinked,
          })}
          saveLabel="Save"
          savingLabel="…"
          onSave={async () => {
            setMsg(null);
            setBusy(true);
            try {
              await onSave({
                title: title.trim() || undefined,
                description: description.trim() || undefined,
                imageUrl: imageUrl.trim() || undefined,
                telegramUrl: telegramUrl.trim() || undefined,
                discordUrl: discordUrl.trim() || undefined,
                websiteUrl: websiteUrl.trim() || undefined,
              });
              setMsg('Saved');
            } catch (e) {
              setMsg(e instanceof Error ? e.message : 'Failed');
            } finally {
              setBusy(false);
            }
          }}
          onSignIn={onSignIn}
          className="w-full py-1 rounded-[2px] bg-amber-500/15 border border-amber-400/30 text-[9px] font-mono uppercase text-amber-100/90"
        />
      </div>
    </details>
  );
}
